import type { DesignMessages } from './sl'

const en = {
    placeholder: 'Editor',

    types: {
        text:    'Text',
        image:   'Image',
        video:   'Video',
        weather: 'Weather',
        clock:   'Clock',
        qr:      'QR code',
        menu:       'Menu',
        happy_hour: 'Happy hour',
    },
    weights: {
        regular:  'Regular',
        medium:   'Medium',
        semibold: 'Semibold',
        bold:     'Bold',
    },
    fit: {
        cover:   'Fill',
        contain: 'Fit',
    },
    align: {
        left:   'Left',
        center: 'Centre',
        right:  'Right',
    },
    role: {
        brand_logo: 'Brand logo',
        guest_logo: 'Guest logo',
    },

    editor: {
        title:            'Screen editor',
        crumb_templates:  'Presentations',
        crumb_editor:     'Editor',
        undo:             'Undo (⌘Z)',
        redo:             'Redo (⇧⌘Z)',
        preview:          'Preview',
        publish:          'Publish',
        published:        'Published · version {version}',
        publish_failed:   'Publishing failed.',
        saved:            'Saved',
        saving:           'Saving …',
        unsaved:          'Unsaved changes',
        save_failed:      'Saving failed.',
        unpublished:      'Unpublished changes',
        load_failed:      'The design could not be loaded.',
        leave_title:      'Unsaved changes',
        leave_message:    'The latest changes could not be saved. Leave the editor anyway?',
        leave_confirm:    'Leave',
        close_preview:    'Close preview (Esc)',

        add_element:      'Add element',
        add_hint:         'Drag onto the screen',

        screen_format:    'Screen {ratio} ({w} × {h})',
        zoom_in:          'Zoom in',
        zoom_out:         'Zoom out',
        zoom_fit:         'Fit to window',

        layers:           'Layers',
        layers_hint:      '(top to bottom)',
        layers_empty:     'No elements on the screen yet.',
        hide:             'Hide',
        show:             'Show',
        lock:             'Lock',
        unlock:           'Unlock',
        drag_layer:       'Drag to reorder',
        rename_hint:      'Double-click to rename',

        properties:       'Properties',
        tab_element:      'Element',
        tab_style:        'Style',
        no_selection:     'Select an element on the screen or add one from the left column.',
        locked_hint:      'The element is locked — it does not move.',
        delete:           'Delete',
        duplicate:        'Duplicate',
        bring_forward:    'Forward',
        send_backward:    'Backward',

        text:             'Text',
        font:             'Font',
        size:             'Size',
        weight:           'Font weight',
        color:            'Colour',
        background:       'Background',
        radius:           'Corners',
        alignment:        'Alignment',
        position:         'Position and size',
        width_short:      'W',
        height_short:     'H',
        rotation:         'Rotation',
        opacity:          'Opacity',
        letter_spacing:   'Letter spacing',
        uppercase:        'Uppercase',
        padding:          'Padding',
        media:            'File',
        choose_media:     'Choose from Media',
        replace_media:    'Replace',
        fit_label:        'Display',
        muted:            'Muted',
        loop:             'Loop',
        show_description: 'Show the weather description',
        own_location:     'Own location',
        own_location_hint: 'Otherwise the weather at the screen\'s location.',
        location_label:   'Place',
        latitude:         'Latitude',
        longitude:        'Longitude',
        clock_format:     'Format',
        with_seconds:     'With seconds',
        without_seconds:  'Without seconds',
        show_date:        'Show the date',
        qr_url:           'Link',
        qr_too_long:      'The link is too long for a QR code.',
        qr_fg:            'Code colour',
        qr_bg:            'Code background',

        transparent:      'Transparent',
        brand_color:      'Brand colour {n}',
        custom_color:     'Custom colour',
        apply:            'Apply',
        white:            'White',
        black:            'Black',

        canvas_background: 'Screen background',
        background_image:  'Background image',
        remove_image:      'Remove image',
        brand_actions:     'Brand',
        brand_actions_hint: 'Align the design with the brand in one click.',
        apply_brand_font:  'Brand font for all text',
        apply_brand_colors: 'Brand colours',

        media_title:      'Choose from Media',
        media_empty:      'There are no files of this kind in Media yet. Upload some first.',
        media_failed:     'Media could not be loaded.',

        new_text:         'New text',

        image_source:      'Picture',
        source_file:       'File',
        source_brand_logo: 'Brand logo',
        source_guest_logo: 'Guest logo',
        role_brand_hint:   "The logo of the screen's look. When the look has no logo, the element is hidden on the screen.",
        role_brand_none:   'This look has no logo — add one under Brand.',
        role_guest_hint:   "The guest's logo during a meeting. Without a meeting or a guest logo, the element is hidden on the screen.",
        brand_font:        'Brand font ({font})',
        brand_font_hint:   "Follows the screen's look.",
        guest_hint:        "{'{guest}'} becomes the guest's name during a meeting.",
        guest_token:       "{'{guest}'}",
        insert_guest:      "Insert {'{guest}'}",
        preview_look:      'Preview look',
        default_look:      'Default look',
    },

    menu: {
        placeholder:            'Menu — connect the Imprion POS',
        empty:                  'The chosen categories have no products.',
        no_price_list:          'Happy hour — the chosen price list is no longer in the POS',
        not_synced:             'The menu has not been imported from the POS yet. Connect the Imprion POS in the settings.',
        title:                  'Title',
        title_hint:             'Empty = no title.',
        source:                 'Source',
        source_categories:      'Categories',
        source_products:        'Chosen products',
        categories:             'Categories',
        categories_hint:        'With none chosen, every category shows, in menu order.',
        categories_placeholder: 'Choose categories',
        products:               'Products',
        products_hint:          'Exactly these products show, in this order.',
        products_placeholder:   'Find products',
        search:                 'Search …',
        product_count:          'Products: {n}',
        missing_item:           'No longer in the POS',
        hidden_item:            'Hidden',
        move_up:                'Up',
        move_down:              'Down',
        remove:                 'Remove',
        columns:                'Columns',
        max_items:              'Most products',
        show_prices:            'Prices',
        show_images:            'Images',
        show_descriptions:      'Descriptions',
        show_tags:              'Tags',
        image_size:             'Photo size',
        image_position:         'Position',
        image_position_left:    'Left',
        image_position_right:   'Right',
        image_position_top:     'Top',
        image_shape:            'Shape',
        image_shape_square:     'Square',
        image_shape_rounded:    'Rounded',
        image_shape_circle:     'Circle',
        happy_hour:             'Happy hour prices',
        happy_hour_hint:        'While a POS price list applies, shows its price and strikes the regular one through.',
        accent:                 'Accent',
        price_list:             'Price list',
        any_list:               'Any active price list',
        any_list_hint:          'Every list but the default one, while it applies.',
        list_off:               'switched off',
        active_now:             'applies now',
        missing_list:           'The price list is no longer in the POS',
        every_day:              'Every day',
        whole_day:              'all day',
        weekdays: {
            1: 'Mon',
            2: 'Tue',
            3: 'Wed',
            4: 'Thu',
            5: 'Fri',
            6: 'Sat',
            7: 'Sun',
        },
        text_active:            'Text during happy hour',
        text_upcoming:          'Text before it starts (today)',
        placeholders_help:      "{'{from}'}, {'{to}'} and {'{name}'} become the start time, the end time and the list's name.",
        upcoming_help:          'Empty = the element is hidden until happy hour applies.',
        show_products:          'Show the products with their happy-hour price',
    },

    playlist: {
        page:               'Page {n} / {count}',
        previous:           'Previous page',
        next:               'Next page',
        play:               'Play',
        pause:              'Pause',
        time_hint:          "{s} s of this page's {total} s",
        none_playable:      'No page of this playlist is published',
        none_playable_hint: 'A screen plays published designs only. Publish at least one in the editor.',
    },

    player: {
        product:        'Imprion DS',
        pair_title:     'Pair this screen',
        pair_network:   'Open the {product} console on the same network — the screen will appear under Screens.',
        pair_network_or_code: 'Or enter the code.',
        pair_pending:   'Waiting to be confirmed in {tenant}',
        pair_pending_or_code: 'or enter the code in the console',
        pair_expires:   'The code is valid until {time}',
        pair_waiting:   'Getting a code …',
        pair_offline:   'No connection to the server. Retrying …',
        loading:        'Loading content …',
        offline:        'Offline — playing the saved content',
        fullscreen_hint: 'Click anywhere for full screen',
    },
} satisfies DesignMessages

export default en
